import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';

import { SapCodeQuotaAvailabilityDto } from '../../api-client';
import { RootState } from '../../application/store';
import { QuotaApiClient } from '../../providers/api-provider';
import {
	clearQuotaAvailability,
	initQuotaAvailability,
	setQuotaAvailabilityLoading,
} from '../../slices/Quota/quotaSlice';

/**
 * Decides whether the backend has to be called.
 * <p>
 * The check on inFlightCustomerId is not redundant with loadedForCustomerId: BVCompactForm lives inside every
 * product card, so on the first load all cards mount in the same commit and none of them has seen the response
 * in redux yet. Without a synchronous guard one fetch per card would go out.
 */
export const shouldFetchQuotaAvailability = (
	customerId: number,
	loadedForCustomerId: number | undefined,
	inFlightCustomerId: number | undefined,
	force: boolean,
): boolean => {
	if (inFlightCustomerId === customerId) return false;
	if (force) return true;

	return loadedForCustomerId !== customerId;
};

// Module state, not component state: it is the only way for every instance of the form to see immediately
// that a request is already on its way.
let inFlightCustomerId: number | undefined;

/** Test only, so a case is not affected by the state left behind by the previous one. */
export const resetQuotaAvailabilityRequestGuard = () => {
	inFlightCustomerId = undefined;
};

export const useQuotaAvailability = () => {
	const { instance } = useMsal();
	const dispatch = useDispatch();

	const availabilityBySapCode = useSelector(
		(state: RootState) => state.quota.availabilityBySapCode,
	);
	const loadedForCustomerId = useSelector(
		(state: RootState) => state.quota.loadedForCustomerId,
	);
	const quotaAvailabilityLoading = useSelector(
		(state: RootState) => state.quota.loading,
	);

	/**
	 * Loads the availability for the given customer, once only even though the form sits inside every card.
	 * @param excludeOrderId order to leave out of the draft quantities, typically the cart being edited.
	 */
	const handleQuotaAvailability = async (
		customerId?: number,
		excludeOrderId?: number,
		force = false,
	) => {
		if (!customerId) {
			dispatch(clearQuotaAvailability());
			return;
		}

		if (
			!shouldFetchQuotaAvailability(
				customerId,
				loadedForCustomerId,
				inFlightCustomerId,
				force,
			)
		)
			return;

		inFlightCustomerId = customerId;
		dispatch(setQuotaAvailabilityLoading(true));

		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = await quotaApi.getQuotaAvailability(
				customerId,
				excludeOrderId,
			);

			dispatch(
				initQuotaAvailability({
					customerId,
					availability: response.data ?? [],
				}),
			);
		} catch {
			// No notification: the quota is side information on the product page. If the backend does not
			// answer the catalogue must stay usable, and the real block is still at order confirmation.
			dispatch(clearQuotaAvailability());
		} finally {
			inFlightCustomerId = undefined;
		}
	};

	/** Returns the quota of a sap code, or undefined when that code is not under quota. */
	const quotaForSapCode = (
		sapCode?: string | null,
	): SapCodeQuotaAvailabilityDto | undefined =>
		sapCode ? availabilityBySapCode[sapCode] : undefined;

	return {
		handleQuotaAvailability,
		quotaForSapCode,
		availabilityBySapCode,
		quotaAvailabilityLoading,
	};
};
